// CinematicHero.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Full-bleed dark photograph + masthead-grade serif overlay. Sets the tone:
// "an invitation, not an application." Single subtle CTA scrolls down to the
// form section near the bottom of the page.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { HERO } from '../data/inclusionContent.js';

const GD   = 'var(--font-heading-primary)';
const NU   = 'var(--font-body)';
const GOLD = '#c9a96e';
const TEXT = '#f0ebe0';
const BODY = 'rgba(240,235,224,0.78)';
const BG   = '#070604';

export default function CinematicHero({ scrollToForm }) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setLoaded(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <section style={{
      position: 'relative',
      width:    '100%',
      height:   'clamp(620px, 92svh, 1000px)',
      overflow: 'hidden',
      background: BG,
    }}>
      {/* Background photograph */}
      {HERO.imageUrl && (
        <img
          src={HERO.imageUrl}
          alt={HERO.imageAlt || ''}
          fetchpriority="high"
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: 'center 35%',
            opacity: loaded ? 1 : 0,
            transition: 'opacity 1.4s ease',
          }}
        />
      )}

      {/* Cinematic gradient, darker at the bottom for type legibility */}
      <div style={{
        position: 'absolute', inset: 0,
        background: `
          linear-gradient(to top, ${BG} 0%, ${BG}e6 18%, ${BG}77 38%, ${BG}33 56%, transparent 75%),
          linear-gradient(to right, ${BG}99 0%, ${BG}33 35%, transparent 70%)
        `,
        pointerEvents: 'none',
      }} />

      {/* Top-left accent, small gold mark to anchor the page */}
      <div style={{
        position: 'absolute', top: 'clamp(120px, 14vw, 160px)', left: 'clamp(28px, 6vw, 80px)',
        display: 'flex', alignItems: 'center', gap: 14,
        opacity: loaded ? 1 : 0,
        transform: loaded ? 'translateY(0)' : 'translateY(8px)',
        transition: 'opacity 0.9s ease 0.2s, transform 0.9s ease 0.2s',
      }}>
        <span style={{ width: 24, height: 1, background: GOLD }} />
        <span style={{
          fontFamily: NU, fontSize: 9, fontWeight: 600,
          letterSpacing: '0.36em', textTransform: 'uppercase',
          color: GOLD,
        }}>
          {HERO.eyebrow}
        </span>
      </div>

      {/* Main content, bottom-left */}
      <div style={{
        position: 'absolute',
        bottom: 0, left: 0, right: 0,
        padding: 'clamp(48px, 7vw, 100px) clamp(28px, 6vw, 96px)',
        maxWidth: 1080,
      }}>
        <h1 style={{
          fontFamily: GD,
          fontSize:   'clamp(48px, 7vw, 110px)',
          fontWeight: 400,
          color:      TEXT,
          lineHeight: 1.02,
          letterSpacing: '-0.02em',
          margin: '0 0 22px',
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(14px)',
          transition: 'opacity 1s ease 0.05s, transform 1s cubic-bezier(0.22,0.61,0.36,1) 0.05s',
        }}>
          {HERO.title}
        </h1>

        <p style={{
          fontFamily: NU,
          fontSize:   'clamp(15px, 1.5vw, 19px)',
          color:      BODY,
          lineHeight: 1.65,
          maxWidth:   620,
          margin:     '0 0 36px',
          opacity:    loaded ? 1 : 0,
          transform:  loaded ? 'translateY(0)' : 'translateY(10px)',
          transition: 'opacity 1s ease 0.25s, transform 1s ease 0.25s',
        }}>
          {HERO.subtitle}
        </p>

        <button
          onClick={scrollToForm}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 14,
            fontFamily: NU, fontSize: 11, fontWeight: 500,
            letterSpacing: '0.28em', textTransform: 'uppercase',
            color: GOLD,
            background: 'none',
            border: `1px solid ${GOLD}`,
            outline: 'none',
            cursor: 'pointer',
            padding: '14px 32px',
            transition: 'background 0.3s ease, color 0.3s ease',
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(8px)',
            transitionDelay: '0.45s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = GOLD; e.currentTarget.style.color = BG; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = GOLD; }}
        >
          {HERO.cta}
          <span aria-hidden style={{
            width: 5, height: 5, background: 'currentColor',
            transform: 'rotate(45deg)',
          }} />
        </button>
      </div>

      {/* Bottom gold rule */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        height: 1,
        background: `linear-gradient(to right, ${GOLD}00, ${GOLD}77, ${GOLD}00)`,
      }} />

      {/* Photo credit, bottom-right, tiny caps. Magazine convention:
          attribution is small and quiet, never competes with the title. */}
      {HERO.photoCredit && (
        <div style={{
          position: 'absolute',
          bottom:   'clamp(20px, 2.4vw, 32px)',
          right:    'clamp(20px, 3vw, 40px)',
          fontFamily: NU,
          fontSize: 8,
          fontWeight: 500,
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          color: 'rgba(240,235,224,0.55)',
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.8s ease 0.7s',
          pointerEvents: 'none',
        }}>
          {HERO.photoCredit}
        </div>
      )}
    </section>
  );
}
